import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { InlineLabel } from 'foundations-components/transitional/components/InlineLabel';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import UIButton from 'UIComponents/button/UIButton';
import UISelect from 'UIComponents/input/UISelect';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TimePeriodDateRange } from './TimePeriodDateRange';
import { CUSTOM_DATE_OPTION, FILTERS, TIME_PERIOD_FILTERS } from '../../constants/FiltersConstants';
import { MONTH } from '../../constants/PipelineCadenceTypes';
import { STARTED, UNINITIALIZED, FAILED } from '../../constants/RequestStatusTypes';
import { getFiscalOffset } from '../../selectors/portalSettings';
import { getPortalWideCadence, getSelectedForecastTypeProperties } from '../../selectors/forecastSelectors';
import { getIsUngatedToForecastingMultipleOutcomes } from '../../selectors/authSelectors';
import { getPropertiesStatus } from '../../selectors/properties';
import { getTimePeriodFilter } from '../../selectors/filtersSelectors';
import { isTimePeriodCustom } from '../../utils/dateUtils';
import { makeTimePeriodFilterOptions } from '../../utils/filters';
import { getCurrentDate } from '../../selectors/dateSelectors';
import { setFilter } from '../../actions/FiltersActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TimePeriodFilterCustom = () => {
  var _useSelector, _useSelector2;
  const dispatch = useDispatch();
  const dateProperty = (_useSelector = useSelector(getSelectedForecastTypeProperties)) === null || _useSelector === void 0 ? void 0 : _useSelector.dateProperty;
  const fiscalOffset = (_useSelector2 = useSelector(getFiscalOffset)) !== null && _useSelector2 !== void 0 ? _useSelector2 : 0;
  const isFmoEnabled = useSelector(getIsUngatedToForecastingMultipleOutcomes);
  const portalWideCadence = useSelector(getPortalWideCadence);
  const propertiesStatus = useSelector(getPropertiesStatus);
  const timePeriodFilter = useSelector(getTimePeriodFilter);
  const currentDate = useSelector(getCurrentDate);
  const options = makeTimePeriodFilterOptions(currentDate, fiscalOffset);
  const [selectValue, setSelectValue] = useState(isTimePeriodCustom(timePeriodFilter) ? CUSTOM_DATE_OPTION : timePeriodFilter);
  const handleTimePeriodChange = useCallback(value => {
    void dispatch(setFilter({
      filter: FILTERS.TIME_PERIOD,
      value
    }));
  }, [dispatch]);
  const handleChangeSelect = useCallback(evt => {
    const value = evt.target.value;
    setSelectValue(value);
    if (value === CUSTOM_DATE_OPTION) {
      return;
    }
    handleTimePeriodChange(value);
  }, [handleTimePeriodChange]);
  const handleOnJumpToCurrentPeriod = useCallback(() => {
    const quarterPeriod = fiscalOffset ? TIME_PERIOD_FILTERS.THIS_FISCAL_QUARTER : TIME_PERIOD_FILTERS.THIS_QUARTER;
    const currentPeriod = portalWideCadence === MONTH ? TIME_PERIOD_FILTERS.THIS_MONTH : quarterPeriod;
    handleTimePeriodChange(currentPeriod);
    setSelectValue(currentPeriod);
  }, [fiscalOffset, portalWideCadence, handleTimePeriodChange]);
  const renderDateLabel = () => {
    if (!isFmoEnabled) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "repOverviewTable.filters.timePeriod.label"
      });
    }
    if (propertiesStatus === UNINITIALIZED || propertiesStatus === STARTED) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        marginInlineStart: "100",
        size: "extra-small"
      });
    }

    // Label the filter as "Date:" if the properties fail to load
    if (propertiesStatus === FAILED) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "repOverviewTable.filters.timePeriod.plainDate"
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [dateProperty, ':']
    });
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "start",
      children: [/*#__PURE__*/_jsx(InlineLabel, {
        label: renderDateLabel(),
        children: /*#__PURE__*/_jsx(UISelect, {
          "aria-labelledby": "rep-filter-timePeriod-label",
          "data-selenium-test": "rep-filter-timePeriod",
          buttonUse: "transparent",
          dropdownFooter: /*#__PURE__*/_jsx(UIButton, {
            "data-test-id": "current-period-link",
            onClick: handleOnJumpToCurrentPeriod,
            use: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "repOverviewTable.filters.timePeriod.jumpToCurrent"
            })
          }),
          menuWidth: 250,
          onChange: handleChangeSelect,
          options: options,
          placeholder:
          /*#__PURE__*/
          // Shouldn't ever display because timePeriodFilter has default value of THIS_MONTH
          _jsx(FormattedMessage, {
            message: "repOverviewTable.filters.timePeriod.placeholder"
          }),
          searchable: false,
          value: selectValue
        })
      }), selectValue === CUSTOM_DATE_OPTION && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(View, {
          marginInline: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "repOverviewTable.filters.timePeriod.fromLabel"
          })
        }), /*#__PURE__*/_jsx(TimePeriodDateRange, {
          onDateSelect: handleTimePeriodChange,
          timePeriodFilterValue: timePeriodFilter
        })]
      })]
    })
  });
};
export default TimePeriodFilterCustom;